import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, finalize, Observable, switchMap, tap} from 'rxjs';
import {environment} from '../../environment/environment';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import type { RecoverPassword, ResetPassword, User } from '../interfaces/user';
import { Brand, FaviconUrl, RoleEnum, type ApiResponse, type Role } from '../interfaces/application';
import { LayoutService } from './layout.service';
import { CookieService } from './cookie.service';

export interface ILoginPayload {
  email: string;
  password: string;
}

export interface IPhoneLoginPayload {
  phone: string;
}

export interface IPhoneLoginValidatePayload extends IPhoneLoginPayload {
  code: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  access_token?: string;
  data: User;
}

interface BasicResponse {
  success: boolean;
  message: string;
}

interface LogoutResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _layoutService: LayoutService = inject(LayoutService);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _cookieService: CookieService = inject(CookieService);
  private readonly _router: Router = inject(Router);

  private readonly _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly _userAuthenticated: BehaviorSubject<User|null> = new BehaviorSubject<User|null>(null);

  private _toggleLoading(): void {
    this._loading.next(!this._loading.value);
  }

  public userAuthenticated(): Observable<User|null> {
    return this._userAuthenticated.asObservable();
  }

  public setUserAuthenticated(user?: User): void {
    this._userAuthenticated.next(user || null);
  }

  public loading(): Observable<boolean> {
    return this._loading.asObservable();
  }

  private _hasPermission = (roles: Role[], permission: string): boolean => {
    return roles.some(role => role.name === RoleEnum.MASTER || role.permissions.some(p => p.name === permission)) || false
  }

  private _loginFirstRouter(user: User): void {
    if (this._hasPermission(user.roles, 'view dashboard')) {
      this._router.navigate(['dashboard']);
      return;
    }
    if (this._hasPermission(user.roles, 'view reports cohort')) {
      this._router.navigate(['reports', 'cohort']);
      return;
    }
    if (this._hasPermission(user.roles, 'get utm bettors')) {
      this._router.navigate(['utm-bettors']);
      return;
    }
    if (this._hasPermission(user.roles, 'view statements')) {
      this._router.navigate(['statements']);
      return;
    }
    if (this._hasPermission(user.roles, 'view payments')) {
      this._router.navigate(['payments']);
      return;
    }
    if (this._hasPermission(user.roles, 'view contracts')) {
      this._router.navigate(['contracts']);
      return;
    }
    if (this._hasPermission(user.roles, 'view announcements')) {
      this._router.navigate(['announcements']);
      return;
    }
    if (this._hasPermission(user.roles, 'view users')) {
      this._router.navigate(['users']);
      return;
    }
    if (this._hasPermission(user.roles, 'view roles')) {
      this._router.navigate(['permissions']);
      return;
    }
  }

  public login(payload: ILoginPayload): void {
    this._toggleLoading();

    this.csrf()
      .pipe(
        switchMap(() => this._login(payload)),
        finalize(() => this._toggleLoading())
      )
      .subscribe({
        next: response => {
          if (response.success) {
            this._loginFirstRouter(response.data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Falha na autenticação',
            detail: error.error.message,
            life: 3000
          });
        }
      });
  }

  public logout(): void {
    this._toggleLoading();

    this._logout()
      .pipe(
        finalize(() => this._toggleLoading()),
        tap((response) => response.success && this._layoutService.initUpdateColors(Brand.LOGAME)),
        tap((response) => response.success && this._layoutService.changeFavicon(FaviconUrl.LOGAME)),
        tap((response) => response.success && this._layoutService.setSystemTitle('Agent View'))
      )
      .subscribe({
        next: response => {
          if (response.success) {
            this._router.navigate(['/auth']);
            this.setUserAuthenticated();
          }
        },
        error: (error: HttpErrorResponse) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao tentar sair',
            detail: error.error.message,
            life: 3000
          });
        }
      })
  }

  private _logout(): Observable<LogoutResponse> {
    return this._http.post<LogoutResponse>(`${environment.api}/auth/logout`, {});
  }

  private _login(payload: ILoginPayload): Observable<LoginResponse> {
    return this._http.post<LoginResponse>(`${environment.api}/auth/login`, payload);
  }

  private _loginByPhone(payload: IPhoneLoginValidatePayload): Observable<LoginResponse> {
    return this._http.post<LoginResponse>(`${environment.api}/auth/login/phone/validate`, payload);
  }

  private _requestPhoneLoginCode(payload: IPhoneLoginPayload): Observable<BasicResponse> {
    return this._http.post<BasicResponse>(`${environment.api}/auth/login/phone`, payload);
  }

  public csrf(): Observable<void> {
    return this._http.get<void>(`${environment.api}/sanctum/csrf-cookie`);
  }

  public requestPhoneLoginCode(payload: IPhoneLoginPayload): Observable<BasicResponse> {
    return this.csrf()
      .pipe(switchMap(() => this._requestPhoneLoginCode(payload)));
  }

  public validatePhoneLogin(payload: IPhoneLoginValidatePayload): Observable<LoginResponse> {
    return this.csrf()
      .pipe(
        switchMap(() => this._loginByPhone(payload)),
        tap((response) => {
          if (response.success) {
            this.setUserAuthenticated(response.data);
            this._loginFirstRouter(response.data);
          }
        })
      );
  }

  public checkCode(code: string): Observable<ApiResponse<User>> {
    return this._http.post<ApiResponse<User>>(`${environment.api}/auth/check-code/${code}`, {});
  }

  public validCode(code: string): Observable<ApiResponse<void>> {
    return this._http.post<ApiResponse<void>>(`${environment.api}/auth/check-code/${code}/valid`, {});
  }

  public sendSmsValidationCode(phone: string): Observable<ApiResponse<void>> {
    return this._http.post<ApiResponse<void>>(`${environment.api}/auth/send-sms-code`, { phone });
  }

  public recoverPassword(payload: RecoverPassword): Observable<ApiResponse<void>> {
    return this._http.post<ApiResponse<void>>(`${environment.api}/auth/recover-password`, payload);
  }

  public resetPassword(code: string, payload: ResetPassword): Observable<ApiResponse<void>> {
    return this._http.post<ApiResponse<void>>(`${environment.api}/auth/reset-password/${code}`, payload);
  }

}
